import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Snackbar,
  Alert,
  useTheme,
  Fade,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

/* Smooth fade and scale-up animation */
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

/* Pulse animation for success state */
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.9; }
  100% { transform: scale(1); opacity: 1; }
`;

const LogoutPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { logout } = useAuth();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loggedOut, setLoggedOut] = useState(false);

  /* Handles logout + graceful transition */
  const handleLogout = useCallback(() => {
    logout();
    setSnackbarOpen(true);
    setLoggedOut(true);

    // Smooth redirect delay
    setTimeout(() => {
      navigate("/login");
    }, 1600);
  }, [logout, navigate]);

  /* Allow pressing Enter key to confirm logout */
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") handleLogout();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleLogout]);

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          px: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 420,
            width: "100%",
            borderRadius: 4,
            boxShadow: loggedOut ? 10 : 6,
            p: 2,
            animation: `${loggedOut ? pulse : fadeInUp} 0.6s ease`,
            transition: "box-shadow 0.3s ease, transform 0.3s ease",
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              gutterBottom
              align="center"
              sx={{ color: theme.palette.text.primary }}
            >
              {loggedOut ? "Logging Out..." : "Confirm Logout"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 3 }}
            >
              {loggedOut
                ? "Please wait while we safely end your session."
                : "Are you sure you want to log out? You’ll need to log in again to access your account."}
            </Typography>
          </CardContent>

          {!loggedOut && (
            <CardActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>
              <Button
                onClick={() => navigate("/")}
                variant="outlined"
                color="inherit"
                sx={{
                  borderRadius: 3,
                  px: 3,
                  fontWeight: 600,
                  "&:hover": { borderColor: theme.palette.primary.main },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleLogout}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  fontWeight: 700,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: theme.palette.error.dark,
                    transform: "scale(1.02)",
                  },
                }}
              >
                Logout
              </Button>
            </CardActions>
          )}
        </Card>

        {/* Snackbar confirmation */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="success"
            variant="filled"
            sx={{
              width: "100%",
              fontWeight: 600,
              background: theme.palette.mode === "dark"
                ? theme.palette.success.dark
                : theme.palette.success.main,
            }}
          >
            You’ve been logged out successfully.
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default LogoutPage;
